import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ReactElement } from 'react-markdown'

import styled, { useTheme } from 'styled-components/native'

import { isOOOEvent } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/utils'
import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { isMinor } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import {
  AppointmentBookedDetails,
  EventExtendedProps,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'

import { Display } from '../../atoms/display/Display'
import { IconBadge } from '../../atoms/iconBadge/IconBadge'
import { AlertIconStroke } from '../../atoms/icons/AlertIconStroke'
import { CheckMarkInCircleIcon } from '../../atoms/icons/CheckMarkInCircleIcon'
import { InfoIcon } from '../../atoms/icons/InfoIcon'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { BadgeShape, BadgeVariant } from '../../badge/constants'
import { DisplaySize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

export type ProviderCalendarEventBadgesProps = {
  title: string
  extendedProps: EventExtendedProps
  hybridBookingMode: boolean
  programName?: ProgramNames
  engageDAProvider?: boolean
  appointmentBookedDetails?: AppointmentBookedDetails
  isDAMinorProgramEnabled?: boolean
  programNameAgeGroup?: string
  conflictTooltipText?: string
}

const BadgeContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

export const ProviderCalendarEventBadges: FunctionComponent<ProviderCalendarEventBadgesProps> = ({
  title,
  extendedProps,
  hybridBookingMode,
  programName,
  engageDAProvider = false,
  appointmentBookedDetails,
  programNameAgeGroup,
  isDAMinorProgramEnabled = false,
  conflictTooltipText,
}) => {
  const intl = useIntl()
  const { colors } = useTheme()
  const badgeTextColor = colors.components.badge.infoSecondary.text

  const clientIsMinor =
    appointmentBookedDetails?.child_isMinor ||
    isMinor(appointmentBookedDetails?.clientDob ? appointmentBookedDetails?.clientDob.replace('DOB: ', '') : '')
  const programNameLabel = programName
    ? getProgramDisplayLabel(intl, programName, ['daClientList', 'short', 'default'])
    : clientIsMinor && engageDAProvider
      ? 'Minor'
      : ''

  // Delay rendering the tooltip to prevent auto-trigger on popover open
  const [tooltipReady, setTooltipReady] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setTooltipReady(true), 300)
    return () => clearTimeout(timer)
  }, [])

  const ageGroupTooltipIcon = useMemo(() => {
    if (!programNameAgeGroup) return undefined
    return tooltipReady ? (
      <Tooltip hoverEnabled placement='top' content={`Ages ${programNameAgeGroup}`}>
        <InfoIcon size={16} fillColor={badgeTextColor} />
      </Tooltip>
    ) : (
      <InfoIcon size={16} fillColor={badgeTextColor} />
    )
  }, [programNameAgeGroup, tooltipReady, badgeTextColor])
  const badges = useMemo(() => {
    const badges: ReactElement[] = []
    if (extendedProps.lyraEventType === 'session' && programName && (!hybridBookingMode || engageDAProvider)) {
      badges.push(
        <IconBadge
          testID={tID(`ProviderCalendarEventBadges-${programName}-badge`)}
          key={programName}
          shape={BadgeShape.RECT}
          variant={BadgeVariant.INFO_SECONDARY}
          text={<Display size={DisplaySize.EYEBROW} text={programNameLabel} />}
          backIcon={ageGroupTooltipIcon}
        />,
      )
    }

    if (
      !isDAMinorProgramEnabled &&
      extendedProps.lyraEventType === 'session' &&
      clientIsMinor &&
      (!hybridBookingMode || engageDAProvider)
    ) {
      badges.push(
        <IconBadge
          testID={tID(`ProviderCalendarEventBadges-minors-badge`)}
          key={'minors'}
          shape={BadgeShape.RECT}
          variant={BadgeVariant.INFO_SECONDARY}
          text={
            <Display
              size={DisplaySize.EYEBROW}
              text={
                <FormattedMessage defaultMessage='Minor' description='Badge label indicating the client is a minor' />
              }
            />
          }
        />,
      )
    }

    if (isOOOEvent(title)) {
      badges.push(
        <IconBadge
          key='ooo'
          shape={BadgeShape.RECT}
          variant={BadgeVariant.SUCCESS}
          text={
            <Display
              size={DisplaySize.EYEBROW}
              text={
                <FormattedMessage
                  defaultMessage='Time off event found'
                  description='Label that appears above event when time off event is found'
                />
              }
            />
          }
          frontIcon={<CheckMarkInCircleIcon size={16} />}
        />,
      )
    }

    if (extendedProps.hasConflict) {
      const conflictBadge = (
        <IconBadge
          key='conflict'
          shape={BadgeShape.RECT}
          variant={BadgeVariant.ALERT}
          text={
            <Display
              size={DisplaySize.EYEBROW}
              text={
                <FormattedMessage
                  defaultMessage='Conflict'
                  description='Label that appears above event when conflict is found'
                />
              }
            />
          }
          frontIcon={<AlertIconStroke size={16} />}
        />
      )
      badges.push(
        conflictTooltipText && tooltipReady ? (
          <Tooltip key='conflict' hoverEnabled placement='top' content={conflictTooltipText}>
            {conflictBadge}
          </Tooltip>
        ) : (
          conflictBadge
        ),
      )
    }

    return badges
  }, [
    extendedProps.lyraEventType,
    extendedProps.hasConflict,
    programName,
    hybridBookingMode,
    engageDAProvider,
    clientIsMinor,
    isDAMinorProgramEnabled,
    title,
    programNameLabel,
    ageGroupTooltipIcon,
    conflictTooltipText,
    tooltipReady,
  ])
  return <BadgeContainer testID={tID('ProviderCalendarEventBadges')}>{badges}</BadgeContainer>
}
